import {
  Button,
  CircularProgress,
  Box,
  Typography,
  Fade,
  TextField,
  MenuItem
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { Fragment, useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { SkuVasDetail } from './types'
import { getVasRuleSku } from '../../pages/VasRule/config/api'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { VasRuleSku } from '../../interfaces/VasRule/VasRule'
import { CaptureVas } from './CaptureVas'

interface SimpleInputModalProps {
  SkuVasDetailData: SkuVasDetail | undefined
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  setTitleModalVas: React.Dispatch<React.SetStateAction<string | undefined>>
  onSaveSuccess?: () => void
  autoSearch?: boolean
}

enum captureVasStep {
  SelectVas = 0, // Selección de Regla VAS asociada a SKU (en caso de existir más de 1 relación)
  CaptureVas = 1 // Captura de Datos VAS asociados al SKU
}

const getTitleByStep = (
  step: captureVasStep,
  titleComplement: string | unknown
): string => {
  switch (step) {
    case captureVasStep.SelectVas:
      return 'Seleccionar Servicio VAS'
    case captureVasStep.CaptureVas:
      return `Captura de Servicio VAS - ${titleComplement}`
    default:
      return ''
  }
}

const useStyles = makeStyles((theme) => ({
  backButton: {
    marginBottom: theme.spacing(2)
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  loadingText: {
    color: theme.palette.text.secondary
  }
}))

export const SelectVasRuleModal: React.FC<SimpleInputModalProps> = ({
  SkuVasDetailData,
  setTitleModalVas,
  handleSnackbar,
  handleCloseModal,
  onSaveSuccess,
  autoSearch = false
}) => {
  const classes = useStyles()
  const [step, setStep] = useState<captureVasStep>(captureVasStep.SelectVas)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedVasRule, setSelectedVasRule] = useState<VasRuleSku | null>(
    null
  )
  const [availableVasRules, setAvailableVasRules] = useState<VasRuleSku[]>([])
  const [selectedVasRuleId, setSelectedVasRuleId] = useState<number | ''>('')

  const { callApi: getRelatedVasRule } = useApi<ApiResponse<VasRuleSku>>()

  // Función para limpiar todos los estados del modal
  const clearAllStates = () => {
    setStep(captureVasStep.SelectVas)
    setIsSearching(false)
    setSelectedVasRule(null)
    setAvailableVasRules([])
  }

  // Buscar VAS relacionados (sin vasRuleId = todos, con vasRuleId = específico)
  const searchRelatedVasRule = async (vasRuleId?: number) => {
    setIsSearching(true)

    const skuId =
      SkuVasDetailData && SkuVasDetailData.skuId ? SkuVasDetailData.skuId : 0

    // Si se proporciona vasRuleId, buscar ese específico
    // Si no, buscar todos los VAS relacionados con el SKU
    getRelatedVasRule(
      getVasRuleSku(skuId, vasRuleId !== undefined ? vasRuleId : undefined)
    )
      .then((result) => {
        const vasRules = result.items || []

        if (vasRules.length === 0) {
          handleSnackbar(
            'No se encontraron servicios VAS para este producto',
            false
          )
          setIsSearching(false)
          return
        }

        if (vasRules.length === 1) {
          // Si solo hay un resultado, ir directo a captura
          setSelectedVasRule(vasRules[0])
          setStep(captureVasStep.CaptureVas)
        } else {
          // Si hay múltiples resultados, guardar para mostrar selector
          setAvailableVasRules(vasRules)
          setStep(captureVasStep.SelectVas)
        }
      })
      .catch((error) => {
        handleSnackbar(`${error}` || 'Error al buscar los servicios VAS', false)
      })
      .finally(() => setIsSearching(false))
  }

  // useEffect para búsqueda automática cuando se abre el modal
  useEffect(() => {
    if (autoSearch && SkuVasDetailData) {
      // Hacer la búsqueda automática sin vasRuleId (obtener todos)
      searchRelatedVasRule()
    }
  }, [autoSearch, SkuVasDetailData])

  // Regresar al paso anterior
  const handleGoBack = () => {
    if (step === captureVasStep.CaptureVas) {
      if (availableVasRules.length > 1) {
        // Si había múltiples opciones, volver a la lista
        setStep(captureVasStep.SelectVas)
        setSelectedVasRule(null)
      } else {
        // Si solo había una opción, cerrar el modal
        handleCloseModal()
      }
    }
  }

  // Seleccionar un VAS de la lista
  const handleSelectVasRule = (vasRuleId: number) => {
    const vasRule = availableVasRules.find((v) => v.vasRuleId === vasRuleId)
    if (vasRule) {
      setSelectedVasRule(vasRule)
      setStep(captureVasStep.CaptureVas)
    }
  }

  // Manejar cambio en el select
  const handleVasRuleChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedVasRuleId(event.target.value as number)
  }

  // Confirmar selección
  const handleConfirmSelection = () => {
    if (selectedVasRuleId) {
      handleSelectVasRule(selectedVasRuleId)
    }
  }

  const handleSaveSuccess = () => {
    clearAllStates()
    onSaveSuccess && onSaveSuccess()
  }

  // Actualizar título cuando cambia el paso
  useEffect(() => {
    setTitleModalVas(getTitleByStep(step, selectedVasRule?.vasRuleName))
  }, [step, selectedVasRule, setTitleModalVas])

  return (
    <Box>
      {/* Paso 1: Seleccionar VAS (cuando hay múltiples resultados) */}
      {step === captureVasStep.SelectVas && (
        <Fade in={step === captureVasStep.SelectVas}>
          <Box>
            {isSearching ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
                <Typography className={classes.loadingText}>
                  Buscando servicios VAS disponibles...
                </Typography>
              </Box>
            ) : (
              <Fragment>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  style={{ marginBottom: 16 }}
                >
                  Se encontraron {availableVasRules.length} servicios VAS
                  disponibles para este producto {SkuVasDetailData?.sku}
                </Typography>

                <Box>
                  <TextField
                    select
                    fullWidth
                    variant='outlined'
                    label='Servicio VAS'
                    value={selectedVasRuleId}
                    onChange={handleVasRuleChange}
                    helperText='Selecciona el servicio VAS que deseas capturar'
                  >
                    {availableVasRules.map((vasRule) => (
                      <MenuItem
                        key={vasRule.vasRuleId}
                        value={vasRule.vasRuleId}
                      >
                        <Box>
                          <Typography
                            variant='body1'
                            style={{ fontWeight: 600 }}
                          >
                            {vasRule.vasRuleName}
                          </Typography>
                          <Typography variant='caption' color='textSecondary'>
                            Código: {vasRule.vasRuleName || '-'} | Marca:{' '}
                            {vasRule.productOriginName || '-'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>

                  <Button
                    fullWidth
                    variant='contained'
                    color='primary'
                    disabled={!selectedVasRuleId}
                    onClick={handleConfirmSelection}
                  >
                    Continuar
                  </Button>
                </Box>
              </Fragment>
            )}
          </Box>
        </Fade>
      )}

      {/* Paso 2: Captura de VAS */}
      {step === captureVasStep.CaptureVas && (
        <Fade in={step === captureVasStep.CaptureVas}>
          <Box>
            <Button
              variant='outlined'
              color='default'
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              className={classes.backButton}
              style={{
                display: availableVasRules.length > 1 ? 'inline-flex' : 'none'
              }}
            >
              {availableVasRules.length > 1 ? 'Cambiar VAS' : 'Cancelar'}
            </Button>

            {selectedVasRule ? (
              <CaptureVas
                VasRuleSku={selectedVasRule}
                SkuVasDetailData={SkuVasDetailData}
                handleSnackbar={handleSnackbar}
                handleCloseModal={handleCloseModal}
                onSaveSuccess={handleSaveSuccess}
              />
            ) : (
              <Box className={classes.loadingContainer}>
                <CircularProgress />
                <Typography className={classes.loadingText}>
                  Cargando información del VAS...
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  )
}
